// components/DashboardHome.js - Чистая версия с оптимизацией загрузки и статистикой доходов

import { useState, useEffect } from 'react';
import { TrendingUp, ArrowRight, ChevronLeft, ChevronRight, User, Users, Wallet, Copy, Share2, Check, UserPlus, Award, Target, Calendar, MessageCircle, Bell, ExternalLink, Clock, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { dashboardService } from '../services/dashboardService';
import TelegramIntegration from './TelegramIntegration';
import { useAuth } from '../contexts/AuthContext';
import Nt from '../components/Nt';

const DashboardHome = () => {
    const { user: authUser, loading: authLoading } = useAuth();
    const [userData, setUserData] = useState(null);
    const [referralLink, setReferralLink] = useState('');
    const [copied, setCopied] = useState(false);
    const [dashboardStats, setDashboardStats] = useState({
        totalIncome: {
            lastMonthUsd: 0,
            totalUsd: 0,
            growth: 0
        },
        partnersInfo: {
            total: 0,
            active: 0,
            totalTeam: null,
            recentPartners: []
        }
    });
    const [isLoading, setIsLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(false);
    const [incomeLoading, setIncomeLoading] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    const usefulLinks = [
        { id: 1, title: 'Чат сообщества', url: 'https://t.me/+EWbDe4ZUdmRlMWJi', icon: 'telegram' },
        { id: 2, title: 'Новости и обновления', url: 'https://t.me/+QIkrGiBoYohiNDBi', icon: 'telegram' },
        { id: 3, title: 'Чеки и отзывы', url: 'https://t.me/+l9Z_C674a285YWYy', icon: 'telegram' },
    ];

    const news = [
        {
            id: 1,
            title: 'Запуск Light Keys Mini',
            content: 'Новая программа Mini Keys уже доступна! Вход $20, выход $80. Присоединяйтесь!',
            date: '2026-02-16'
        },
        {
            id: 2,
            title: 'Запуск нового направления Light Miks',
            content: 'Мы рады объявить о скором запуске нового направления в рамках нашей платформы.',
            date: '2026-01-30'
        },
        {
            id: 3,
            title: 'Обновление платформы',
            content: 'Улучшена скорость работы, добавлены новые функции в личном кабинете.',
            date: '2026-02-10'
        }
    ];

    // Быстрая загрузка основных данных
    useEffect(() => {
        if (authUser) {
            const teamStats = authUser.team_statistics;

            setUserData(authUser);
            setReferralLink(authUser.referral_link || '');

            setDashboardStats({
                totalIncome: {
                    lastMonthUsd: authUser.total_earned_usd || 0,
                    totalUsd: authUser.total_earned_usd || 0,
                    growth: authUser.income_growth || 0
                },
                partnersInfo: {
                    total: authUser.partners_count || 0,
                    active: authUser.active_partners_count || 0,
                    totalTeam: null,
                    recentPartners: teamStats?.recent_partners || []
                }
            });

            setIsLoading(false);
        }
    }, [authUser]);

    // Загрузка полной статистики команды
    useEffect(() => {
        if (authUser && !isLoading) {
            const timer = setTimeout(() => {
                fetchFullTeamStats();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [authUser, isLoading]);

    // Загрузка статистики доходов
    useEffect(() => {
        if (authUser && !isLoading) {
            const timer = setTimeout(() => {
                fetchIncomeStats();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [authUser, isLoading]);

    const fetchFullTeamStats = async () => {
        try {
            setStatsLoading(true);
            const response = await authService.getTeamStats();

            if (response.success) {
                const teamStats = response.data;
                setDashboardStats(prev => ({
                    ...prev,
                    partnersInfo: {
                        ...prev.partnersInfo,
                        totalTeam: teamStats.all_team?.total || 0,
                        recentPartners: teamStats.recent_partners || prev.partnersInfo.recentPartners
                    }
                }));
            } else {
                setDashboardStats(prev => ({
                    ...prev,
                    partnersInfo: {
                        ...prev.partnersInfo,
                        totalTeam: prev.partnersInfo.total
                    }
                }));
            }
        } catch (err) {
            console.error('Ошибка загрузки статистики команды:', err);
            setDashboardStats(prev => ({
                ...prev,
                partnersInfo: {
                    ...prev.partnersInfo,
                    totalTeam: prev.partnersInfo.total
                }
            }));
        } finally {
            setStatsLoading(false);
        }
    };

    const fetchIncomeStats = async () => {
        try {
            setIncomeLoading(true);
            const token = localStorage.getItem('auth_token');
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

            const response = await fetch(`${API_URL}/dashboard/stats`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error('Ошибка загрузки статистики');

            const data = await response.json();

            if (data.success && data.data.income_statistics) {
                const incomeStats = data.data.income_statistics;
                setDashboardStats(prev => ({
                    ...prev,
                    totalIncome: {
                        lastMonthRub: incomeStats.last_month_income_rub || 0,
                        totalRub: incomeStats.total_income_rub || 0,
                        lastMonthUsd: incomeStats.last_month_income_usd || 0,
                        totalUsd: incomeStats.total_income_usd || 0,
                        growth: incomeStats.growth_percentage || 0
                    }
                }));
            }
        } catch (err) {
            console.error('Ошибка загрузки статистики доходов:', err);
        } finally {
            setIncomeLoading(false);
        }
    };

    const refreshTeamStats = () => {
        setDashboardStats(prev => ({
            ...prev,
            partnersInfo: { ...prev.partnersInfo, totalTeam: null }
        }));
        fetchFullTeamStats();
    };

    const refreshIncomeStats = () => {
        setIncomeLoading(true);
        fetchIncomeStats();
    };

    const copyReferralLink = () => {
        if (referralLink) {
            navigator.clipboard.writeText(referralLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % news.length);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + news.length) % news.length);

    const formatCurrency = (amount) => {
        if (!amount || amount === 0) return '0.00';
        return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const getIconComponent = (iconName) => {
        switch (iconName) {
            case 'telegram': return <MessageCircle size={16} className="text-blue-500" />;
            default: return <ExternalLink size={16} className="text-gray-500" />;
        }
    };

    if (authLoading || isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Загрузка...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">

            {/* Приветствие */}
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl text-white p-6 shadow-lg">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold mb-1">
                            Добро пожаловать, <Nt>{userData?.first_name || userData?.login || 'Партнёр'}</Nt>!
                        </h1>
                        <p className="text-white/80 text-sm">
                            Ваш личный кабинет на платформе ЛАЙТ
                        </p>
                    </div>
                    <div className="hidden md:block">
                        <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                            {userData?.avatar ? (
                                <img
                                    src={userData.avatar}
                                    alt="Аватар"
                                    className="w-16 h-16 rounded-full object-cover"
                                />
                            ) : (
                                <User size={32} className="text-white" />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Статистика - 4 карточки */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                {/* Общие доходы */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-all">
                    <div className="flex items-center justify-between mb-3">
                        <div className="p-2.5 bg-emerald-50 rounded-lg">
                            <TrendingUp className="text-emerald-600" size={20} />
                        </div>
                        <div className="flex items-center gap-2">
                            {incomeLoading && (
                                <Clock className="text-gray-400 animate-pulse" size={14} />
                            )}
                            {!incomeLoading && dashboardStats.totalIncome.growth !== 0 && (
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                    dashboardStats.totalIncome.growth > 0
                                        ? 'bg-emerald-50 text-emerald-700'
                                        : 'bg-red-50 text-red-700'
                                }`}>
                                    {dashboardStats.totalIncome.growth > 0 ? '+' : ''}
                                    {Math.abs(dashboardStats.totalIncome.growth).toFixed(1)}%
                                </span>
                            )}
                            {!incomeLoading && (
                                <button
                                    onClick={refreshIncomeStats}
                                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                    title="Обновить"
                                >
                                    <RefreshCw size={12} className="text-gray-400" />
                                </button>
                            )}
                        </div>
                    </div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Общие доходы</p>
                    {incomeLoading ? (
                        <div className="h-7 w-24 bg-gray-100 rounded animate-pulse"></div>
                    ) : (
                        <>
                            <p className="text-2xl font-bold text-gray-900">
                                {formatCurrency(dashboardStats.totalIncome.totalUsd)}
                                <span className="text-sm font-medium text-gray-500 ml-1">USDT</span>
                            </p>
                            {dashboardStats.totalIncome.lastMonthUsd > 0 && (
                                <p className="text-xs text-gray-400 mt-1">
                                    За месяц: {formatCurrency(dashboardStats.totalIncome.lastMonthUsd)} USDT
                                </p>
                            )}
                        </>
                    )}
                </div>

                {/* Прямые партнёры */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-all">
                    <div className="flex items-center justify-between mb-3">
                        <div className="p-2.5 bg-blue-50 rounded-lg">
                            <UserPlus className="text-blue-600" size={20} />
                        </div>
                        {dashboardStats.partnersInfo.active > 0 && (
                            <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 rounded-full">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>
                                <span className="text-xs text-blue-700">{dashboardStats.partnersInfo.active} акт.</span>
                            </div>
                        )}
                    </div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Прямые партнёры</p>
                    <p className="text-2xl font-bold text-gray-900">
                        {dashboardStats.partnersInfo.total}
                        <span className="text-sm font-medium text-gray-500 ml-1">чел.</span>
                    </p>
                </div>

                {/* Общая команда */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-all">
                    <div className="flex items-center justify-between mb-3">
                        <div className="p-2.5 bg-amber-50 rounded-lg">
                            <Users className="text-amber-600" size={20} />
                        </div>
                        {statsLoading && dashboardStats.partnersInfo.totalTeam === null && (
                            <Clock className="text-gray-400 animate-pulse" size={14} />
                        )}
                    </div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Общая команда</p>
                    {statsLoading && dashboardStats.partnersInfo.totalTeam === null ? (
                        <div className="h-7 w-16 bg-gray-100 rounded animate-pulse"></div>
                    ) : (
                        <p className="text-2xl font-bold text-gray-900">
                            {dashboardStats.partnersInfo.totalTeam || 0}
                            <span className="text-sm font-medium text-gray-500 ml-1">чел.</span>
                        </p>
                    )}
                </div>

                {/* Баланс */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-all">
                    <div className="flex items-center justify-between mb-3">
                        <div className="p-2.5 bg-teal-50 rounded-lg">
                            <Wallet className="text-teal-600" size={20} />
                        </div>
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-teal-50 rounded-full">
                            <div className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse"></div>
                            <span className="text-xs text-teal-700">Доступно</span>
                        </div>
                    </div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Ваш баланс</p>
                    <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(userData?.balance_usd || 0)}
                        <span className="text-sm font-medium text-gray-500 ml-1">USDT</span>
                    </p>
                </div>
            </div>

            {/* Основной контент */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Левая колонка */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Реферальная ссылка */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="text-base font-semibold text-gray-800 mb-1 flex items-center gap-2">
                            <Share2 size={18} className="text-teal-600" />
                            Ваша реферальная ссылка
                        </h3>
                        <p className="text-sm text-gray-500 mb-4">
                            Поделитесь ссылкой и получайте бонусы за каждого партнёра
                        </p>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={referralLink}
                                readOnly
                                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                            />
                            <button
                                onClick={copyReferralLink}
                                className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                                    copied
                                        ? 'bg-emerald-500 text-white'
                                        : 'bg-teal-600 text-white hover:bg-teal-700'
                                }`}
                            >
                                {copied ? (
                                    <><Check size={16} /> Скопировано</>
                                ) : (
                                    <><Copy size={16} /> Копировать</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Последние партнёры */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-semibold text-gray-800">Последние партнёры</h3>
                            <Link
                                to="/dashboard/partners"
                                className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
                            >
                                Все партнёры
                                <ArrowRight size={14} />
                            </Link>
                        </div>

                        {dashboardStats.partnersInfo.recentPartners.length > 0 ? (
                            <div className="space-y-2">
                                {dashboardStats.partnersInfo.recentPartners.slice(0, 5).map((partner) => (
                                    <div key={partner.id} className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                        <div className="flex items-center">
                                            {partner.avatar && partner.avatar !== false ? (
                                                <img
                                                    src={`https://api.light-platform.com/storage/${partner.avatar}`}
                                                    alt={partner.login}
                                                    className="w-9 h-9 rounded-full object-cover mr-3"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                className={`w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center mr-3 ${
                                                    partner.avatar && partner.avatar !== false ? 'hidden' : ''
                                                }`}
                                            >
                                                <span className="text-teal-700 text-xs font-semibold">
                                                    {partner.full_name?.split(' ').map(n => n[0]).join('') || partner.login?.[0] || 'P'}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">
                                                    <Nt>{partner.full_name || partner.login}</Nt>
                                                </p>
                                                <p className="text-xs text-gray-400">
                                                    {partner.registration_date ? new Date(partner.registration_date).toLocaleDateString('ru-RU') : ''}
                                                </p>
                                            </div>
                                        </div>
                                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                                            partner.status === 'active'
                                                ? 'bg-emerald-50 text-emerald-700'
                                                : 'bg-gray-100 text-gray-500'
                                        }`}>
                                            {partner.status === 'active' ? 'Активен' : 'Неактивен'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-10 text-gray-400">
                                <Users size={40} className="mx-auto mb-3 text-gray-300" />
                                <p className="text-sm">Пока нет партнёров</p>
                                <p className="text-xs mt-1">Поделитесь реферальной ссылкой</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Правая колонка */}
                <div className="space-y-6">
                    {/* Telegram интеграция */}
                    <TelegramIntegration user={userData} isProfilePage={false} />

                    {/* Полезные ссылки */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="text-base font-semibold text-gray-800 mb-4">Полезные ссылки</h3>
                        <div className="space-y-2">
                            {usefulLinks.map((link) => (
                                <a
                                    key={link.id}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                                >
                                    {getIconComponent(link.icon)}
                                    <span className="text-sm text-gray-700 group-hover:text-gray-900 flex-1 ml-2.5">
                                        {link.title}
                                    </span>
                                    <ExternalLink size={12} className="text-gray-300 group-hover:text-gray-500" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Новости */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-semibold text-gray-800">Новости</h3>
                            <div className="flex gap-1">
                                <button onClick={prevSlide} className="p-1 rounded hover:bg-gray-100 transition-colors">
                                    <ChevronLeft size={16} className="text-gray-500" />
                                </button>
                                <button onClick={nextSlide} className="p-1 rounded hover:bg-gray-100 transition-colors">
                                    <ChevronRight size={16} className="text-gray-500" />
                                </button>
                            </div>
                        </div>

                        {news.length > 0 && (
                            <div className="space-y-3">
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 text-sm mb-1.5">
                                        {news[currentSlide].title}
                                    </h4>
                                    <p className="text-xs text-gray-600 mb-2 leading-relaxed">
                                        {news[currentSlide].content}
                                    </p>
                                    <div className="flex items-center text-xs text-gray-400">
                                        <Calendar size={11} className="mr-1" />
                                        {new Date(news[currentSlide].date).toLocaleDateString('ru-RU')}
                                    </div>
                                </div>

                                <div className="flex justify-center gap-1">
                                    {news.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentSlide(index)}
                                            className={`w-1.5 h-1.5 rounded-full transition-colors ${
                                                index === currentSlide ? 'bg-teal-500' : 'bg-gray-200'
                                            }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Быстрые действия */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="text-base font-semibold text-gray-800 mb-4">Быстрые действия</h3>
                        <div className="space-y-2">
                            <Link
                                to="/dashboard/profile"
                                className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                            >
                                <User className="text-teal-600 mr-3" size={16} />
                                <span className="text-sm text-gray-700 group-hover:text-gray-900">Редактировать профиль</span>
                                <ArrowRight className="ml-auto text-gray-300 group-hover:text-gray-500" size={14} />
                            </Link>
                            <Link
                                to="/dashboard/partners"
                                className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                            >
                                <Users className="text-blue-600 mr-3" size={16} />
                                <span className="text-sm text-gray-700 group-hover:text-gray-900">Управление командой</span>
                                <ArrowRight className="ml-auto text-gray-300 group-hover:text-gray-500" size={14} />
                            </Link>
                            <Link
                                to="/dashboard/finance"
                                className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                            >
                                <Target className="text-amber-600 mr-3" size={16} />
                                <span className="text-sm text-gray-700 group-hover:text-gray-900">Финансы и выплаты</span>
                                <ArrowRight className="ml-auto text-gray-300 group-hover:text-gray-500" size={14} />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardHome;
